/**
 * WordPressConnectionDetail — the WordPress card's on-demand detail: the
 * connect flow EXTRACTED from TenantSettingsPage (the not-connected branch
 * of the legacy WP card). Direct save first (works for non-Cloudflare
 * sites); on a 502 (backend can't reach WP through Cloudflare/WAF) it
 * falls back to the SSE reverse-verification flow where the plugin calls
 * us, with the same 5-minute timeout. All user-visible strings are
 * legacy-verbatim; the success patches (['tenantSettings'] setQueryData +
 * notifyWordPressConnectionWritten) mirror the legacy card exactly.
 */

import { useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Alert, Button, Input, Space, message } from 'antd';
import { apiService } from '@/services/api';
import { notifyWordPressConnectionWritten } from '@/hooks/useClientCapabilities';
import type { TenantSettings } from '@/types/tenant';

export function WordPressConnectionDetail({ tenantId }: { tenantId: string }) {
  const queryClient = useQueryClient();
  const [secretKeyInput, setSecretKeyInput] = useState('');
  const [savingKey, setSavingKey] = useState(false);
  const [verificationState, setVerificationState] = useState<
    'idle' | 'waiting' | 'verified' | 'timeout' | 'error'
  >('idle');
  const [verificationMessage, setVerificationMessage] = useState('');
  const eventSourceRef = useRef<EventSource | null>(null);

  // Clean up EventSource on unmount
  useEffect(() => {
    return () => {
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
        eventSourceRef.current = null;
      }
    };
  }, []);

  return (
    <div className="flex flex-col gap-3">
      {verificationState === 'idle' && (
        <Alert
          type="info"
          showIcon
          message="Paste the secret key from your WordPress plugin settings."
          description={<span>In WordPress, go to <strong>Plugins &rarr; AgileCO Platform &rarr; Settings</strong> and copy the secret key shown there.</span>}
        />
      )}
      {verificationState === 'waiting' && (
        <Alert
          type="warning"
          showIcon
          message="Waiting for plugin verification..."
          description={<span>Click <strong>"Verify Connection"</strong> in your WordPress plugin settings to complete the connection.</span>}
        />
      )}
      {verificationState === 'verified' && (
        <Alert
          type="success"
          showIcon
          message="Connection verified!"
          description="Your WordPress site is now connected."
        />
      )}
      {verificationState === 'timeout' && (
        <Alert
          type="error"
          showIcon
          message="Verification timed out"
          description={verificationMessage || "Please try again and click 'Verify Connection' in WordPress within 5 minutes."}
        />
      )}
      {verificationState === 'error' && (
        <Alert
          type="error"
          showIcon
          message="Connection failed"
          description={verificationMessage || "Please check your secret key and try again."}
        />
      )}
      <Space.Compact style={{ width: '100%' }}>
        <Input.Password
          placeholder="sk_agileco_..."
          value={secretKeyInput}
          onChange={(e) => setSecretKeyInput(e.target.value)}
          style={{ flex: 1 }}
          disabled={verificationState === 'waiting'}
        />
        <Button
          type="primary"
          loading={savingKey}
          disabled={!secretKeyInput.trim() || verificationState === 'waiting'}
          onClick={async () => {
            if (!secretKeyInput.trim()) return;
            setSavingKey(true);
            setVerificationState('idle');
            setVerificationMessage('');

            // Close any existing SSE connection
            if (eventSourceRef.current) {
              eventSourceRef.current.close();
              eventSourceRef.current = null;
            }

            try {
              // Try direct flow first (works for non-Cloudflare sites)
              const result = await apiService.saveWordPressKey(secretKeyInput.trim(), tenantId);
              if (result.wp_connected) {
                setVerificationState('verified');
                queryClient.setQueryData(['tenantSettings', tenantId], (old: TenantSettings | null) => old ? { ...old, wp_connected: true } : old);
                notifyWordPressConnectionWritten(queryClient, tenantId, true);
                message.success(result.verified !== false
                  ? 'WordPress connected and verified!'
                  : 'Key saved. Connection will be verified when publishing.');
                setSecretKeyInput('');
                setSavingKey(false);
                setTimeout(() => setVerificationState('idle'), 2000);
                return;
              }
            } catch (err: unknown) {
              const status = (err as { response?: { status?: number } })?.response?.status;
              if (status !== 502) {
                setVerificationState('error');
                setVerificationMessage((err as { response?: { data?: { detail?: string } } })?.response?.data?.detail || 'Failed to save key. Please check and try again.');
                setSavingKey(false);
                return;
              }
              // 502 = backend can't reach WP (Cloudflare/WAF) — fall through to SSE
            }

            // Reverse flow: open SSE and wait for plugin to call us
            setVerificationState('waiting');
            const evtSource = apiService.waitForPluginVerification(secretKeyInput.trim(), tenantId);
            eventSourceRef.current = evtSource;

            const closeAndCleanup = () => {
              evtSource.close();
              eventSourceRef.current = null;
            };

            evtSource.onmessage = (event) => {
              try {
                const data = JSON.parse(event.data);
                if (data.status === 'verified') {
                  closeAndCleanup();
                  setVerificationState('verified');
                  queryClient.setQueryData(['tenantSettings', tenantId], (old: TenantSettings | null) => old ? { ...old, wp_connected: true } : old);
                  notifyWordPressConnectionWritten(queryClient, tenantId, true);
                  message.success('WordPress connected successfully');
                  setSecretKeyInput('');
                  setSavingKey(false);
                  setTimeout(() => setVerificationState('idle'), 2000);
                } else if (data.status === 'timeout') {
                  closeAndCleanup();
                  setVerificationState('timeout');
                  setVerificationMessage(data.message);
                  setSavingKey(false);
                } else if (data.status === 'error') {
                  closeAndCleanup();
                  setVerificationState('error');
                  setVerificationMessage(data.message);
                  setSavingKey(false);
                }
              } catch {
                // Ignore parse errors
              }
            };

            evtSource.onerror = () => {
              closeAndCleanup();
              setVerificationState('error');
              setVerificationMessage('Connection error. Please try again.');
              setSavingKey(false);
            };

            // BUG 3 fix: use evtSource.readyState instead of stale closure
            setTimeout(() => {
              if (evtSource.readyState !== EventSource.CLOSED) {
                closeAndCleanup();
                setVerificationState('timeout');
                setVerificationMessage('Verification timed out. Please try again.');
                setSavingKey(false);
              }
            }, 310000);
          }}
        >
          {verificationState === 'waiting' ? 'Waiting...' : 'Connect'}
        </Button>
      </Space.Compact>
    </div>
  );
}
